// src/components/mkh/WhoAnswered.js
import React, { useState } from "react";
import { FaXmark } from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import { encryptData } from "../../funcs/encryption";
import { questionShown } from "../../api/game";
import { useMutation } from "@tanstack/react-query";
import madImage from "../../assets/images/mad.png";

const MKHWhoAnswered = ({ game, setGame, question, setShowWhoAnswered }) => {
  const [selectedTeam, setSelectedTeam] = useState(null); // Single team selection
  const navigate = useNavigate();
  const { partnerSlug } = useParams();
  // Keep the /partner/:slug prefix when navigating within a partner page.
  const partnerBase = partnerSlug ? `/partner/${partnerSlug}` : "";

  const modeName = game?.modes?.find((mode) =>
    mode.questions.find(
      (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
    )
  )?.name;

  // Get the actual question object from the game
  const questionInGame = question?.custom
    ? game.modes
        .find((mode) =>
          mode.questions.find(
            (q) => (q.question?._id || q?.customQuestion?._id) === question?._id
          )
        )
        .questions.find(
          (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
        ).customQuestion
    : game.modes
        .find((mode) =>
          mode.questions.find(
            (q) => (q.question?._id || q?.customQuestion?._id) === question?._id
          )
        )
        .questions.find(
          (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
        ).question;

  const checkIfAllQuestionsAnswered = () => {
    let answered = true;
    game?.modes?.forEach((mode) => {
      mode.questions.forEach((q) => {
        if (!q.question?.answered && !q.customQuestion?.answered) {
          answered = false;
        }
      });
    });
    return answered;
  };

  const nextTurn = () => {
    const currentTurnIndex = game.teams.findIndex((team) => team.currentTurn);
    const nextTurnIndex =
      currentTurnIndex === game.teams.length - 1 ? 0 : currentTurnIndex + 1;

    const updatedTeams = game.teams.map((team, index) => ({
      ...team,
      currentTurn: index === nextTurnIndex,
    }));

    // Update game with new turn
    const updatedGame = { ...game, teams: updatedTeams };
    setGame(updatedGame);
    localStorage.setItem("game", encryptData(updatedGame));
  };

  const { mutate } = useMutation({
    mutationKey: ["questionShown"],
    mutationFn: ({ questionId, isAnswered }) =>
      questionShown(questionId, isAnswered),
  });

  const handleQuestionShown = (hasBeenAnswered) => {
    mutate({
      questionId: questionInGame?._id,
      isAnswered: hasBeenAnswered,
    });
  };

  const handleTeamSelect = (teamId) => {
    // Toggle selection - if same team clicked, deselect; otherwise select new team
    setSelectedTeam(selectedTeam === teamId ? null : teamId);
  };

  const handleNoAnswer = () => {
    setSelectedTeam("no-answer");
  };

  const handleSave = () => {
    if (selectedTeam && selectedTeam !== "no-answer") {
      // Award points to the selected team
      const updatedGame = { ...game };
      const teamIndex = updatedGame.teams.findIndex(
        (team) => team._id === selectedTeam
      );

      if (teamIndex !== -1 && question?.points > 0) {
        updatedGame.teams[teamIndex].points += question?.points;
      }

      // Mark question as answered
      questionInGame.answered = true;

      setGame(updatedGame);
      localStorage.setItem("game", encryptData(updatedGame));

      // API call and navigation
      handleQuestionShown(true);
      nextTurn();

      if (checkIfAllQuestionsAnswered()) {
        navigate(`${partnerBase}/${game?.codeInfo?.code || "old"}/end`);
      } else {
        navigate(`${partnerBase}/${game?.codeInfo?.code || "old"}`);
      }
    } else if (selectedTeam === "no-answer") {
      // Just mark as answered without awarding points
      questionInGame.answered = true;

      const updatedGame = { ...game };
      setGame(updatedGame);
      localStorage.setItem("game", encryptData(updatedGame));

      // API call and navigation
      handleQuestionShown(false);
      nextTurn();

      if (checkIfAllQuestionsAnswered()) {
        navigate(`${partnerBase}/${game?.codeInfo?.code || "old"}/end`);
      } else {
        navigate(`${partnerBase}/${game?.codeInfo?.code || "old"}`);
      }
    }
  };

  return (
    <div className="w-full h-[90vh] bg-[#eaf6fb] flex flex-col">
      {/* Header Card - Thinner */}
      <div className="p-2 flex-shrink-0">
        <div className="bg-white rounded-xl p-3 shadow-lg border-2 border-[#bfe4f3]">
          <div className="flex justify-between items-center">
            {/* Left - Mode Info */}
            <div className="flex items-center space-x-3">
              <div className="bg-[#29a6d6] rounded-lg px-3 py-1">
                <h2 className="text-white font-bold text-sm">{modeName}</h2>
              </div>
              <div className="bg-[#bfe4f3] rounded-lg px-3 py-1">
                <h2 className="text-[#29a6d6] font-bold text-sm">
                  {question?.points} نقطة
                </h2>
              </div>
              <div className="bg-[#29a6d6] rounded-lg px-3 py-1">
                <h2 className="text-white font-bold text-sm">من جاوب؟</h2>
              </div>
              {question?.custom && (
                <div className="bg-[#eaf6fb] border-2 border-[#29a6d6] rounded-lg px-2 py-1">
                  <span className="text-[#29a6d6] font-semibold text-xs">
                    فئة خاصة
                  </span>
                </div>
              )}
            </div>

            {/* Right - Close Button */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowWhoAnswered(false)}
                className="bg-[#bfe4f3] hover:bg-[#a8d4e8] text-[#29a6d6] p-2 rounded-lg transition-colors"
              >
                <FaXmark size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-2 flex-1 min-h-0">
        <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-[#bfe4f3] h-full flex flex-col">
          {/* Teams Selection */}
          <div className="mb-4 flex-1 min-h-0">
            <h3 className="text-[#29a6d6] font-bold mb-3 text-lg text-center">
              اختر الفريق الذي أجاب بشكل صحيح:
            </h3>
            <div className="h-full">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 h-5/6">
                {/* Team Buttons */}
                {game?.teams?.map((team, index) => (
                  <button
                    key={team._id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex flex-col justify-center items-center ${
                      selectedTeam === team._id
                        ? "bg-[#29a6d6] border-[#29a6d6] text-white shadow-lg"
                        : "bg-[#eaf6fb] border-[#bfe4f3] text-[#29a6d6] hover:bg-[#d5eff8] hover:border-[#a8d4e8]"
                    }`}
                    onClick={() => handleTeamSelect(team._id)}
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-current mb-2">
                      <img
                        src={team?.image?.image}
                        alt={team?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="font-bold text-sm text-center mb-1">
                      {team.name}
                    </h4>
                    <p className="text-xs opacity-80 mb-2">
                      {team.points} نقطة
                    </p>

                    {selectedTeam === team._id && (
                      <div className="mt-2 text-xs opacity-90 text-center">
                        ✓ سيحصل على {question?.points || 0} نقطة
                      </div>
                    )}
                  </button>
                ))}

                {/* No Answer Button */}
                <button
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex flex-col justify-center items-center ${
                    selectedTeam === "no-answer"
                      ? "bg-red-500 border-red-500 text-white shadow-lg"
                      : "bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300"
                  }`}
                  onClick={handleNoAnswer}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-current bg-white p-2 mb-2">
                    <img
                      src={madImage}
                      alt="زعلان"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="font-bold text-sm text-center mb-1">
                    محد جاوب
                  </h4>
                  <p className="text-xs opacity-80 text-center">
                    لم يجب أي فريق
                  </p>
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-3 flex-shrink-0">
            <button
              onClick={() => setShowWhoAnswered(false)}
              className="px-6 py-2 bg-[#bfe4f3] text-[#29a6d6] rounded-lg hover:bg-[#a8d4e8] font-bold text-sm transition-colors"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedTeam}
              className={`px-6 py-2 rounded-lg font-bold text-sm transition-colors ${
                !selectedTeam
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#29a6d6] text-white hover:bg-[#1e7ba6] shadow-lg"
              }`}
            >
              حفظ وإنهاء السؤال
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MKHWhoAnswered;
